// Painter für den Hafen (Außenszenen): Kai bei Nacht, Kneipenfassade, Kutter, Fischbude, Poller, Laterne, Möwe.
import { P } from './palette.ts';
import { reg, drawDoor, FONT_DISPLAY, FONT_HAND } from './painters.ts';
import { type Ctx, Rng, shade, mix, rgba, vgrad, hgrad, radial, glow, dabs, roundRect, poly, woodGrain, contactShadow, grain, text } from './paint.ts';

/** Nachthimmel mit Sternen, Mond und Wolkenbändern */
export function nightSky(ctx: Ctx, rng: Rng, W: number, H: number, moonX: number, moonY: number) {
  vgrad(ctx, 0, 0, W, H, [
    [0, P.night0],
    [0.55, P.night1],
    [1, P.night2],
  ]);
  for (let i = 0; i < W / 9; i++) {
    const a = rng.range(0.15, 0.8);
    ctx.fillStyle = rgba(P.amber3, a);
    const s = rng.chance(0.1) ? 2.4 : 1.4;
    ctx.fillRect(rng.next() * W, rng.next() * H * 0.8, s, s);
  }
  glow(ctx, moonX, moonY, 220, P.amber3, 0.18);
  ctx.fillStyle = mix(P.paper, P.amber3, 0.4);
  ctx.beginPath();
  ctx.arc(moonX, moonY, 34, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = rgba(P.plaster1, 0.25);
  for (const [dx, dy, r] of [[-10, -6, 7], [8, 10, 5], [12, -12, 4]]) {
    ctx.beginPath();
    ctx.arc(moonX + dx, moonY + dy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Wolkenbänder
  for (let i = 0; i < 9; i++) {
    const y = rng.range(H * 0.1, H * 0.7), x = rng.range(-200, W);
    ctx.fillStyle = rgba(rng.chance(0.5) ? P.night3 : P.night2, rng.range(0.12, 0.28));
    ctx.beginPath();
    ctx.ellipse(x, y, rng.range(200, 480), rng.range(12, 30), 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Kai: Himmel, ferne Stadt, Leuchtturm, Wasser, Kaimauer, Kopfsteinpflaster */
reg('quay', {
  size: (p) => ({ w: p.width ?? 2400, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 2400, H = p.height ?? 1080;
    const horizon = p.horizon ?? 520;
    const quayY = p.quayY ?? 720;
    nightSky(ctx, rng, W, horizon, W * 0.72, 140);
    // ferne Stadt / Hügel
    ctx.fillStyle = P.night1;
    ctx.beginPath();
    ctx.moveTo(0, horizon);
    for (let x = 0; x <= W; x += 40) ctx.lineTo(x, horizon - 40 - Math.sin(x * 0.004) * 25 - rng.range(0, 10));
    ctx.lineTo(W, horizon);
    ctx.fill();
    for (let i = 0; i < W / 26; i++) {
      const bx = rng.next() * W, bh = rng.range(20, 90), bw = rng.range(18, 50);
      ctx.fillStyle = mix(P.night0, P.night1, rng.next());
      ctx.fillRect(bx, horizon - bh, bw, bh);
      if (rng.chance(0.25)) {
        poly(ctx, [bx - 2, horizon - bh, bx + bw / 2, horizon - bh - rng.range(10, 24), bx + bw + 2, horizon - bh]);
        ctx.fill();
      }
      for (let k = 0; k < 3; k++) {
        if (rng.chance(0.35)) {
          const lx = bx + rng.range(3, bw - 5), ly = horizon - rng.range(6, bh - 4);
          ctx.fillStyle = rng.chance(0.85) ? P.amber2 : P.white;
          ctx.fillRect(lx, ly, 3, 4);
        }
      }
    }
    // Kirchturm
    ctx.fillStyle = P.night0;
    const kx = W * 0.4;
    ctx.fillRect(kx, horizon - 170, 34, 170);
    poly(ctx, [kx - 4, horizon - 170, kx + 17, horizon - 250, kx + 38, horizon - 170]);
    ctx.fill();
    // Leuchtturm
    const lx = p.lighthouseX ?? W * 0.9;
    ctx.fillStyle = P.night0;
    poly(ctx, [lx - 18, horizon - 6, lx + 18, horizon - 6, lx + 11, horizon - 150, lx - 11, horizon - 150]);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.fillRect(lx - 15, horizon - 60, 30, 14);
    ctx.fillRect(lx - 13, horizon - 110, 26, 14);
    ctx.fillStyle = P.amber3;
    ctx.fillRect(lx - 9, horizon - 168, 18, 16);
    ctx.fillStyle = P.night0;
    poly(ctx, [lx - 13, horizon - 168, lx, horizon - 186, lx + 13, horizon - 168]);
    ctx.fill();
    // Wasser
    vgrad(ctx, 0, horizon, W, quayY - horizon, [
      [0, P.night2],
      [0.3, P.sea0],
      [1, P.night0],
    ]);
    for (let i = 0; i < 260; i++) {
      const y = horizon + Math.pow(rng.next(), 0.8) * (quayY - horizon);
      const len = 10 + ((y - horizon) / (quayY - horizon)) * 60;
      ctx.fillStyle = rgba(rng.chance(0.6) ? P.night3 : P.sea2, rng.range(0.15, 0.4));
      ctx.fillRect(rng.next() * W, y, len, 2);
    }
    // Spiegelungen der Stadtlichter
    for (let i = 0; i < 70; i++) {
      const x = rng.next() * W, y = horizon + rng.range(4, 80);
      ctx.fillStyle = rgba(P.amber2, rng.range(0.2, 0.55));
      ctx.fillRect(x, y, rng.range(4, 14), 1.6);
    }
    // Mondpfad
    for (let i = 0; i < 40; i++) {
      const y = horizon + rng.range(0, quayY - horizon - 20);
      const spread = 30 + (y - horizon) * 0.3;
      ctx.fillStyle = rgba(P.amber3, rng.range(0.1, 0.35));
      ctx.fillRect(W * 0.72 + rng.range(-spread, spread), y, rng.range(10, 40), 2);
    }
    // Kaimauer-Kante
    ctx.fillStyle = shade(P.iron1, -0.2);
    ctx.fillRect(0, quayY - 26, W, 26);
    for (let x = 0; x < W; x += 70) {
      ctx.fillStyle = shade(P.iron1, rng.range(-0.25, 0.05));
      ctx.fillRect(x + 2, quayY - 24, 66, 22);
    }
    ctx.fillStyle = rgba(P.amber3, 0.12);
    ctx.fillRect(0, quayY - 26, W, 3);
    // Pflaster
    vgrad(ctx, 0, quayY, W, H - quayY, [
      [0, shade(P.iron1, -0.35)],
      [1, shade(P.iron1, -0.1)],
    ]);
    for (let row = 0; row < 40; row++) {
      const t = row / 40;
      const y = quayY + Math.pow(t, 1.5) * (H - quayY);
      const s = 10 + t * 38;
      for (let x = -s; x < W + s; x += s * 1.9) {
        ctx.fillStyle = shade(P.iron2, rng.range(-0.55, -0.2));
        ctx.beginPath();
        ctx.ellipse(x + (row % 2) * s + rng.range(-3, 3), y, s * 0.85, s * 0.36, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // Nässe-Glanz
    for (let i = 0; i < 30; i++) radial(ctx, rng.next() * W, rng.range(quayY + 40, H), rng.range(40, 120), [[0, rgba(P.night3, 0.18)], [1, rgba(P.night3, 0)]]);
    grain(ctx, 0, quayY, W, H - quayY, 0.15);
    vgrad(ctx, 0, quayY, W, 60, [
      [0, rgba(P.night0, 0.5)],
      [1, rgba(P.night0, 0)],
    ]);
  },
});

/** Kneipenfassade mit Tür, Fenstern, Schild */
reg('pubfront', {
  size: (p) => ({ w: p.w ?? 720, h: p.h ?? 760 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 720, h = p.h ?? 760;
    // Backstein
    ctx.fillStyle = shade(P.red0, -0.3);
    ctx.fillRect(0, 60, w, h - 60);
    for (let y = 60; y < h; y += 26) {
      for (let x = (y / 26) % 2 ? -30 : 0; x < w; x += 60) {
        ctx.fillStyle = shade(rng.pick([P.red0, P.red1, P.wood2]), rng.range(-0.5, -0.25));
        ctx.fillRect(x + 2, y + 2, 56, 22);
      }
    }
    dabs(ctx, rng, 0, 60, w, h - 60, [P.night1, P.red0], { count: 500, alpha: [0.05, 0.15] });
    // Giebel
    ctx.fillStyle = shade(P.red0, -0.45);
    poly(ctx, [-10, 70, w / 2, -40, w + 10, 70]);
    ctx.fill();
    ctx.fillStyle = P.wood0;
    poly(ctx, [-20, 76, w / 2, -52, w + 20, 76, w + 20, 62, w / 2, -66, -20, 62]);
    ctx.fill();
    // Fenster
    for (const fx of [70, w - 250]) {
      ctx.fillStyle = P.wood1;
      ctx.fillRect(fx - 10, 330, 200, 210);
      vgrad(ctx, fx, 340, 180, 190, [
        [0, P.amber2],
        [1, P.amber0],
      ]);
      ctx.fillStyle = rgba(P.wood0, 0.75);
      ctx.fillRect(fx + 85, 340, 10, 190);
      ctx.fillRect(fx, 430, 180, 10);
      // Silhouetten drinnen
      ctx.fillStyle = rgba(P.wood0, 0.5);
      ctx.beginPath();
      ctx.ellipse(fx + 50, 500, 22, 40, 0, 0, Math.PI * 2);
      ctx.fill();
      glow(ctx, fx + 90, 435, 200, P.amber2, 0.3);
    }
    // Obergeschoss-Fenster (dunkel)
    for (const fx of [120, w - 220]) {
      ctx.fillStyle = P.wood1;
      ctx.fillRect(fx - 8, 130, 116, 140);
      vgrad(ctx, fx, 138, 100, 124, [
        [0, P.night2],
        [1, P.night0],
      ]);
    }
    // Tür
    drawDoor(ctx, rng, { x: w / 2, w: 150, h: 300, floorY: h, color: P.sea1 });
    ctx.fillStyle = P.amber2;
    ctx.fillRect(w / 2 - 50, h - 290, 100, 18);
    glow(ctx, w / 2, h - 300, 120, P.amber2, 0.4);
    // Schild
    const sx = w / 2 - 170;
    const fresh = p.state === true; // neues Schild gekauft (Ausbau „sign“)
    if (fresh) glow(ctx, w / 2, 316, 220, P.amber2, 0.35);
    ctx.fillStyle = fresh ? P.night1 : P.wood0;
    roundRect(ctx, sx, 286, 340, 60, 10);
    ctx.fill();
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 3;
    roundRect(ctx, sx + 6, 292, 328, 48, 8);
    ctx.stroke();
    text(ctx, p.sign ?? 'Zum Rostigen Anker', w / 2, 317, `700 30px ${FONT_DISPLAY}`, fresh ? P.amber3 : P.brass2);
    // Anker-Emblem
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(w / 2, 230, 26, 0.2, Math.PI - 0.2);
    ctx.moveTo(w / 2, 180);
    ctx.lineTo(w / 2, 256);
    ctx.moveTo(w / 2 - 16, 196);
    ctx.lineTo(w / 2 + 16, 196);
    ctx.stroke();
    // Regenrinne, Schatten
    hgrad(ctx, w - 80, 60, 80, h - 60, [
      [0, rgba(P.night0, 0)],
      [1, rgba(P.night0, 0.6)],
    ]);
    grain(ctx, 0, 0, w, h, 0.12);
  },
});

/** Fischkutter im Wasser */
reg('boat', {
  size: () => ({ w: 620, h: 420 }),
  draw(ctx, p, rng) {
    // Rumpf
    ctx.fillStyle = P.sea1;
    ctx.beginPath();
    ctx.moveTo(10, 300);
    ctx.lineTo(600, 280);
    ctx.quadraticCurveTo(580, 370, 520, 390);
    ctx.lineTo(90, 395);
    ctx.quadraticCurveTo(30, 370, 10, 300);
    ctx.fill();
    ctx.fillStyle = P.white;
    ctx.fillRect(30, 300, 560, 12);
    ctx.fillStyle = P.red1;
    ctx.beginPath();
    ctx.moveTo(60, 372);
    ctx.lineTo(560, 362);
    ctx.lineTo(540, 388);
    ctx.lineTo(90, 394);
    ctx.fill();
    text(ctx, p.name ?? 'MÖWE II', 300, 345, `700 26px ${FONT_DISPLAY}`, P.paper);
    // Steuerhaus
    ctx.fillStyle = P.paper;
    ctx.fillRect(380, 180, 140, 120);
    ctx.fillStyle = P.red1;
    ctx.fillRect(370, 168, 160, 18);
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = P.night1;
      ctx.fillRect(392 + i * 42, 200, 32, 34);
    }
    // Mast & Netze
    ctx.fillStyle = P.wood1;
    ctx.fillRect(200, 40, 10, 260);
    ctx.strokeStyle = rgba(P.paper, 0.6);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(205, 50);
    ctx.lineTo(40, 290);
    ctx.moveTo(205, 50);
    ctx.lineTo(380, 180);
    ctx.stroke();
    ctx.strokeStyle = rgba(P.green2, 0.6);
    for (let i = 0; i < 12; i++) {
      ctx.beginPath();
      ctx.moveTo(205, 90 + i * 8);
      ctx.lineTo(120 + i * 4, 290);
      ctx.stroke();
    }
    // Positionslampe
    ctx.fillStyle = P.amber3;
    ctx.beginPath();
    ctx.arc(205, 40, 6, 0, Math.PI * 2);
    ctx.fill();
    glow(ctx, 205, 40, 50, P.amber2, 0.5);
    // Spiegelung im Wasser
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = P.sea2;
    ctx.fillRect(40, 398, 520, 14);
    ctx.fillRect(80, 414, 440, 6);
    ctx.restore();
    vgrad(ctx, 0, 280, 620, 120, [
      [0, rgba(P.night0, 0)],
      [1, rgba(P.night0, 0.45)],
    ]);
    grain(ctx, 0, 0, 620, 420, 0.1);
    void rng;
  },
});

/** Fischbude mit Markise */
reg('fishhut', {
  size: () => ({ w: 460, h: 420 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 230, 412, 460, 40, 0.6);
    for (let i = 0; i < 8; i++) woodGrain(ctx, rng, 20 + i * 52, 120, 52, 290, shade(P.sea1, rng.range(-0.25, 0)), false);
    // Theke
    woodGrain(ctx, rng, 0, 250, 460, 40, P.wood3, true);
    ctx.fillStyle = rgba(P.night0, 0.5);
    ctx.fillRect(0, 290, 460, 10);
    // Markise
    for (let i = 0; i < 9; i++) {
      ctx.fillStyle = i % 2 ? P.white : P.red1;
      poly(ctx, [i * 52, 70, (i + 1) * 52, 70, (i + 1) * 52 + 6, 130, i * 52 + 6, 130]);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(i * 52 + 32, 130, 26, 0, Math.PI);
      ctx.fill();
    }
    // Dach
    ctx.fillStyle = P.wood0;
    poly(ctx, [-16, 72, 230, 0, 476, 72]);
    ctx.fill();
    // Schild
    ctx.fillStyle = P.paper;
    roundRect(ctx, 110, 18, 240, 44, 8);
    ctx.fill();
    text(ctx, p.sign ?? 'Gesas Fischbude', 230, 41, `700 26px ${FONT_HAND}`, P.red1);
    // Fische an der Leine
    for (let i = 0; i < 5; i++) {
      const fx = 70 + i * 70;
      ctx.strokeStyle = P.ink;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(fx, 150);
      ctx.lineTo(fx, 170);
      ctx.stroke();
      ctx.fillStyle = mix(P.iron2, P.brass2, rng.next() * 0.5);
      ctx.beginPath();
      ctx.ellipse(fx, 196, 9, 26, 0, 0, Math.PI * 2);
      ctx.fill();
      poly(ctx, [fx - 10, 226, fx + 10, 226, fx, 214]);
      ctx.fill();
    }
    // Lampe
    ctx.fillStyle = P.amber3;
    ctx.beginPath();
    ctx.arc(400, 150, 12, 0, Math.PI * 2);
    ctx.fill();
    glow(ctx, 400, 150, 160, P.amber2, 0.45);
    // Eiskiste mit Fischen
    ctx.fillStyle = P.white;
    ctx.fillRect(40, 232, 150, 20);
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = mix(P.iron2, P.white, 0.5);
      ctx.beginPath();
      ctx.ellipse(55 + i * 24, 236, 12, 5, 0.2, 0, Math.PI * 2);
      ctx.fill();
    }
  },
});

/** Poller (optional mit Tau) */
reg('bollard', {
  size: () => ({ w: 140, h: 150 }),
  draw(ctx, p) {
    contactShadow(ctx, 70, 144, 130, 22, 0.55);
    ctx.fillStyle = P.iron0;
    ctx.fillRect(40, 40, 60, 104);
    ctx.beginPath();
    ctx.ellipse(70, 40, 44, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.iron1;
    ctx.beginPath();
    ctx.ellipse(70, 36, 42, 13, 0, 0, Math.PI * 2);
    ctx.fill();
    hgrad(ctx, 40, 40, 60, 104, [
      [0, rgba(P.iron2, 0.4)],
      [1, rgba(P.night0, 0.4)],
    ]);
    if (p.rope !== false) {
      ctx.strokeStyle = P.plaster2;
      ctx.lineWidth = 9;
      ctx.beginPath();
      ctx.ellipse(70, 80, 36, 12, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(100, 84);
      ctx.quadraticCurveTo(130, 120, 120, 146);
      ctx.stroke();
      ctx.strokeStyle = rgba(P.wood1, 0.6);
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 5]);
      ctx.beginPath();
      ctx.ellipse(70, 80, 36, 12, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  },
});

/** Gaslaterne */
reg('lanternpost', {
  size: () => ({ w: 120, h: 560 }),
  draw(ctx) {
    contactShadow(ctx, 60, 554, 100, 20, 0.5);
    ctx.fillStyle = P.iron0;
    ctx.fillRect(52, 110, 16, 440);
    ctx.fillRect(38, 520, 44, 34);
    ctx.fillRect(30, 96, 60, 14);
    ctx.fillStyle = P.amber3;
    poly(ctx, [38, 96, 82, 96, 92, 30, 28, 30]);
    ctx.fill();
    ctx.fillStyle = P.iron0;
    poly(ctx, [20, 32, 100, 32, 60, 0]);
    ctx.fill();
    ctx.strokeStyle = P.iron0;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(60, 30);
    ctx.lineTo(60, 96);
    ctx.stroke();
    glow(ctx, 60, 64, 110, P.amber2, 0.6);
  },
});

/** Möwe (sitzend) */
reg('gull', {
  size: () => ({ w: 80, h: 70 }),
  draw(ctx, p) {
    if (p.flip) {
      ctx.translate(80, 0);
      ctx.scale(-1, 1);
    }
    ctx.fillStyle = P.white;
    ctx.beginPath();
    ctx.ellipse(40, 40, 24, 16, -0.15, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(58, 24, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.iron1;
    ctx.beginPath();
    ctx.ellipse(34, 36, 20, 9, -0.25, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = P.amber1;
    poly(ctx, [66, 24, 78, 27, 66, 29]);
    ctx.fill();
    ctx.fillStyle = P.ink;
    ctx.fillRect(59, 21, 3, 3);
    ctx.strokeStyle = P.amber1;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(36, 54);
    ctx.lineTo(36, 68);
    ctx.moveTo(46, 54);
    ctx.lineTo(46, 68);
    ctx.stroke();
  },
});

/** Bank */
reg('bench', {
  size: () => ({ w: 280, h: 140 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 140, 134, 270, 22, 0.5);
    woodGrain(ctx, rng, 10, 20, 260, 18, P.wood3, true);
    woodGrain(ctx, rng, 10, 44, 260, 18, P.wood3, true);
    woodGrain(ctx, rng, 10, 76, 260, 16, P.wood2, true);
    ctx.fillStyle = P.iron0;
    ctx.fillRect(30, 20, 10, 114);
    ctx.fillRect(240, 20, 10, 114);
  },
});

/** Glocke an der Theke (aufgehängt) */
reg('bell', {
  size: () => ({ w: 120, h: 170, ax: 60, ay: 0 }),
  draw(ctx) {
    ctx.strokeStyle = P.plaster2;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(60, 0);
    ctx.lineTo(60, 40);
    ctx.stroke();
    const g = ctx.createLinearGradient(20, 0, 100, 0);
    g.addColorStop(0, P.brass0);
    g.addColorStop(0.4, P.brass2);
    g.addColorStop(1, P.brass0);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(44, 44);
    ctx.quadraticCurveTo(30, 60, 30, 110);
    ctx.quadraticCurveTo(26, 132, 14, 140);
    ctx.lineTo(106, 140);
    ctx.quadraticCurveTo(94, 132, 90, 110);
    ctx.quadraticCurveTo(90, 60, 76, 44);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = P.brass0;
    ctx.beginPath();
    ctx.arc(60, 152, 10, 0, Math.PI * 2);
    ctx.fill();
    glow(ctx, 50, 90, 60, P.amber2, 0.3);
  },
});

// Inventar-Icons für Kapitel 1
reg('icon', {
  size: () => ({ w: 96, h: 96, ax: 48, ay: 48 }),
  draw(ctx, p) {
    const k = p.kind;
    const brass = () => {
      const g = ctx.createLinearGradient(20, 0, 76, 0);
      g.addColorStop(0, P.brass0);
      g.addColorStop(0.45, P.brass2);
      g.addColorStop(1, P.brass0);
      return g;
    };
    if (k === 'bell' || k === 'bellk' || k === 'quizbell') {
      ctx.fillStyle = brass();
      ctx.beginPath();
      ctx.moveTo(38, 22);
      ctx.quadraticCurveTo(28, 34, 28, 58);
      ctx.quadraticCurveTo(26, 70, 16, 76);
      ctx.lineTo(80, 76);
      ctx.quadraticCurveTo(70, 70, 68, 58);
      ctx.quadraticCurveTo(68, 34, 58, 22);
      ctx.closePath();
      ctx.fill();
      if (k !== 'bell') {
        ctx.fillStyle = P.brass0;
        ctx.beginPath();
        ctx.arc(48, 82, 7, 0, Math.PI * 2);
        ctx.fill();
      }
      if (k === 'quizbell') {
        ctx.strokeStyle = P.plaster2;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(48, 22);
        ctx.quadraticCurveTo(70, 4, 86, 14);
        ctx.stroke();
        glow(ctx, 48, 50, 40, P.amber2, 0.35);
      }
    } else if (k === 'clapper') {
      ctx.strokeStyle = P.iron1;
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(30, 20);
      ctx.lineTo(60, 66);
      ctx.stroke();
      ctx.fillStyle = P.brass1;
      ctx.beginPath();
      ctx.arc(64, 72, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.iron2;
      ctx.beginPath();
      ctx.arc(30, 20, 7, 0, Math.PI * 2);
      ctx.fill();
    } else if (k === 'rope') {
      ctx.strokeStyle = P.plaster2;
      ctx.lineWidth = 9;
      ctx.beginPath();
      ctx.ellipse(48, 52, 30, 20, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(74, 60);
      ctx.quadraticCurveTo(90, 80, 80, 90);
      ctx.stroke();
      ctx.strokeStyle = rgba(P.wood1, 0.6);
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 5]);
      ctx.beginPath();
      ctx.ellipse(48, 52, 30, 20, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  },
});
